import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { Card, CardHeader, ContentLoader } from '@/components/common';
import { getErrorMessage } from '@/services/api';
import {
  channelPreferencesApi,
  type ChannelCategory,
  type ChannelName,
  type ChannelPreferences,
} from '@/services/channelPreferences';

const KEY = ['communications', 'channel-preferences'] as const;

const CATEGORY_LABELS: Record<ChannelCategory, string> = {
  quotes: 'Quotes',
  policy_documents: 'Policy documents',
  renewal_reminders: 'Renewal reminders',
};

const CHANNEL_LABELS: Record<ChannelName, string> = {
  email: 'Email',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  push: 'Push',
};

/**
 * Which channels reach the customer for quotes, policy documents and renewal
 * reminders. Only the channels the server lists as `delivering` (email today)
 * send anything; the others are saved and labelled "coming soon" so a switch
 * is never presented as live when it is not.
 */
export function NotificationChannelsCard() {
  const client = useQueryClient();
  const query = useQuery({ queryKey: KEY, queryFn: () => channelPreferencesApi.get() });
  const save = useMutation({
    mutationFn: (vars: { category: ChannelCategory; channel: ChannelName; value: boolean }) =>
      channelPreferencesApi.update({ [vars.category]: { [vars.channel]: vars.value } }),
    onSuccess: (data: ChannelPreferences) => client.setQueryData(KEY, data),
    onError: (error) => toast.error(getErrorMessage(error)),
  });
  const data = query.data;

  return (
    <Card>
      <CardHeader
        title="Notification channels"
        subtitle="Choose how we reach you about quotes, policy documents and renewal reminders"
      />
      {query.isLoading ? (
        <ContentLoader />
      ) : query.isError || !data ? (
        <div className="text-sm text-red-600 dark:text-red-400">
          Could not load your notification channels.{' '}
          <button type="button" className="underline" onClick={() => query.refetch()}>
            Try again
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 dark:text-gray-400">
                <th className="py-2 pr-4 font-medium">&nbsp;</th>
                {data.channels.map((channel) => (
                  <th key={channel} className="py-2 px-3 font-medium text-center">
                    {CHANNEL_LABELS[channel]}
                    {!data.delivering.includes(channel) && (
                      <span className="block text-xs font-normal text-gray-500 dark:text-gray-400">coming soon / saved</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.categories.map((category) => (
                <tr key={category} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="py-3 pr-4 font-medium text-gray-900 dark:text-gray-100">{CATEGORY_LABELS[category]}</td>
                  {data.channels.map((channel) => (
                    <td key={channel} className="py-3 px-3 text-center">
                      <input
                        type="checkbox"
                        aria-label={`${CATEGORY_LABELS[category]} by ${CHANNEL_LABELS[channel]}`}
                        checked={data.preferences[category][channel]}
                        disabled={save.isPending}
                        onChange={(e) => save.mutate({ category, channel, value: e.target.checked })}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            Only email is sent today. SMS, WhatsApp and push choices are saved and will take effect once those
            channels go live.
          </p>
        </div>
      )}
    </Card>
  );
}
